import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { getLegalContent } from "../config/legalContent";

/**
 * Renders a single legal page section. Supports plain paragraphs (with
 * newline preservation) plus an optional bulleted list underneath.
 */
const Section = ({ h, p, list }) => (
  <>
    <h2>{h}</h2>
    {p ? <p style={{ whiteSpace: "pre-line" }}>{p}</p> : null}
    {list && list.length ? (
      <ul className="list-disc pl-5 space-y-1 text-[#6B7A66]">
        {list.map((item) => <li key={item}>{item}</li>)}
      </ul>
    ) : null}
  </>
);

const LegalLayout = ({ eyebrow, title, disclaimer, children, testid }) => (
  <main data-testid={testid} className="bg-[#F6F5F2] min-h-screen">
    <section className="pt-28 lg:pt-36 pb-24 lg:pb-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="overline text-[#A7CA99] mb-5">{eyebrow}</div>
        <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.05] text-[#3E5239]">
          {title}
        </h1>
        {disclaimer ? (
          <p
            data-testid="legal-binding-disclaimer"
            className="mt-8 text-xs italic font-light text-[#6B7A66] border-l-2 border-[#A7CA99] pl-4"
          >
            {disclaimer}
          </p>
        ) : null}
        <div className="prose prose-sm mt-10 max-w-none text-[#3E5239] font-light leading-relaxed [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-light [&_h2]:text-[#3E5239] [&_h2]:mt-10 [&_h2]:mb-3 [&_p]:text-[#6B7A66] [&_a]:text-[#3E5239] [&_a]:underline">
          {children}
        </div>
      </div>
    </section>
  </main>
);

const LEGAL_SEO = {
  impressum: {
    testid: "impressum-page",
    seo: { title: "Impressum · KosMedico Zürich", description: "Impressum da KosMedico Medizinische Ästhetik — Thurgauerstrasse 117, 8152 Zürich.", canonicalPath: "/impressum" },
    eyebrowKey: "legal.impressum_eyebrow",
    eyebrowFallback: "Informação Legal",
  },
  agb: {
    testid: "agb-page",
    seo: { title: "AGB · Allgemeine Geschäftsbedingungen · KosMedico Zürich", description: "Allgemeine Geschäftsbedingungen der KosMedico Zürich für ästhetische Behandlungen.", canonicalPath: "/agb" },
    eyebrowKey: "legal.agb_eyebrow",
    eyebrowFallback: "Termos",
  },
  datenschutz: {
    testid: "datenschutz-page",
    seo: { title: "Datenschutz · KosMedico Zürich", description: "Datenschutzerklärung der KosMedico Zürich gemäss revDSG und DSGVO.", canonicalPath: "/datenschutz" },
    eyebrowKey: "legal.datenschutz_eyebrow",
    eyebrowFallback: "Privacidade",
  },
};

/** Shared renderer: picks localized content based on the active i18n language. */
const LegalPage = ({ page }) => {
  const { t, i18n } = useTranslation();
  const cfg = LEGAL_SEO[page];
  useSeo(cfg.seo);
  const content = getLegalContent(page, i18n.language);
  return (
    <LegalLayout
      testid={cfg.testid}
      eyebrow={t(cfg.eyebrowKey, cfg.eyebrowFallback)}
      title={content.title}
      disclaimer={content.bindingDisclaimer}
    >
      {content.sections.map((s) => <Section key={s.h} {...s} />)}
    </LegalLayout>
  );
};

export const ImpressumPage = () => <LegalPage page="impressum" />;
export const AgbPage = () => <LegalPage page="agb" />;
export const DatenschutzPage = () => <LegalPage page="datenschutz" />;
